<template>
  <div id="lm-upload-files" v-if="showComponent" class="container">
    <div class="row">
      <div class="col-12 mb-2" v-if="files.length">
        <div class="mt-0 d-flex justify-content-between">
          <h4 class="font-weight-normal">Archivos cargados</h4>
          <div v-if="files.length" class="d-flex justify-content-end align-items-center mr-5">
            <b-badge :variant="totalSizeIndicator">{{ totalSize }}</b-badge>
          </div>
        </div>
        <draggable
          tag="b-list-group"
          v-if="files.length"
          v-model="files"
          @start="drag=true"
          @end="drag=false"
          animation="150"
          handle=".handle"
        >
          <b-list-group-item
            v-for="(file, index) in files"
            :key="index"
            class="list-group-item d-flex justify-content-between align-items-center py-2 handle"
          >
            <div class="d-flex align-items-center">
              <font-awesome-icon class="mr-4" icon="arrows-alt" style="width:18px; height:18px;" />
              <span class="circle d-inline-flex justify-content-center align-items-center mr-4">{{index + 1}}</span>
              <b-button
                v-b-modal="`file_modal_${id}`"
                class="my-0"
                variant="link"
                size="sm"
                @click="setFileInModalPreview(file, index)"
              >
                {{file.name}}
              </b-button>
            </div>
            <div class="d-flex justify-content-end align-items-center">
              <b-badge :variant="sizeIndicator(file.size)">{{ formatBytes(file.size) }}</b-badge>
              <font-awesome-icon class="ml-3 trash-icon" icon="trash" @click="removeUploadFile(file)"/>
            </div>
          </b-list-group-item>
        </draggable>
      </div>
      <div class="col-12">
        <label
          :for="id"
          class="text-truncate"
        >
          {{label}} <small v-if="required">*</small>
        </label>
        <font-awesome-icon
          id="font-awesome-icon-edit"
          icon="edit"
          size="sm"
          class="clickable float-right mt-1"
          v-if="showEditButton"
          @click="$emit('edit-button-clicked')"
        />
        <font-awesome-icon
          icon="question-circle"
          size="sm"
          class="clickable float-right mt-1"
          :class="{ 'mr-2': showEditButton }"
          v-if="allowedExtensionsHelperText"
          v-b-tooltip.left.hover="allowedExtensionsHelperText"
        />
        <vue-dropzone
          ref="dropzoneComponent"
          :class="{'is-invalid': v.$error, 'disabled': dropzoneOptions.maxFiles === 0}"
          :id="id"
          :options="dropzoneOptions"
          :duplicateCheck="duplicateCheck"
          v-on:vdropzone-removed-file="fileRemoved"
          v-on:vdropzone-success="fileCreated"
          v-on:vdropzone-error="errorHappend"
          v-on:vdropzone-file-added="fileAdded"
          v-on:vdropzone-duplicate-file="fileDuplicated"
          v-on:vdropzone-processing="processing"
          v-on:vdropzone-queue-complete="complete"
          :useCustomSlot="true"
        >
        <div v-if="dropzoneOptions.maxFiles !== 0">
          <h4 class="dropzone-custom-title">{{$t('drop_files_here_to_upload')}}</h4>
          <p>...o haz click para seleccionar los archivos de tu computadora</p>
          <p>
            Puedes cargar{{dropzoneOptions.maxFiles > 0 ? ` hasta ${dropzoneOptions.maxFiles}` : '' }} archivo{{dropzoneOptions.maxFiles === 1 ? '' : 's'}} de {{dropzoneOptions.maxFilesize}} Mbs {{dropzoneOptions.maxFiles === 1 ? '' : 'c/u como máximo'}}.
          </p>
        </div>
        <div v-else>
          <h4>Componente deshabilitado</h4>
          <p>No es posible cargar archivos en este componente.</p>
        </div>
        </vue-dropzone>
        <p class="invalid-feedback d-inline mt-2" v-if="!v.required && v.$error"> {{ $t('field_required')}} </p>
        <b-button
          class="my-2 float-right"
          variant="link"
          size="sm"
          @click="removeAllFilesModal"
        >
          Limpiar todos los archivos
        </b-button>
      </div>
    </div>
    <LmConfirmationModal ref="removeAllFilesModal" title="Esta acción no puede deshacerse"  @ok="lmRemoveAllFiles" />
    <b-modal :id="`file_modal_${id}`" :title="fileModalPreview.name" size="xl" hide-footer >
      <div class="d-flex align-items-center">
        <b-button
          class="position-absolute prev-btn"
          variant="secondary"
          @click="onPrevFile"
          :disabled="fileModalPreview.index === 0"
        >
          <i class="mdi mdi-chevron-left"></i>
        </b-button>
        <iframe :src="fileModalPreview.src" frameborder="0" style="height:75vh;" width="100%"></iframe>
        <b-button
          class="position-absolute next-btn"
          variant="secondary"
          @click="onNextFile"
          :disabled="fileModalPreview.index >= files.length - 1"
        >
          <i class="mdi mdi-chevron-right"></i>
        </b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import LmConfirmationModal from '@/components/modals/LmConfirmationModal.vue'
import vueDropzone from 'vue2-dropzone'
import 'vue2-dropzone/dist/vue2Dropzone.min.css'
import draggable from 'vuedraggable'
import lmInputMixin from '@/mixins/lmInputMixin.js'
import lmToasts from '@/mixins/lmToasts.js'
import { acceptedFilesGenerator } from '@/helpers/LmUploadFiles'
import imageCompression from 'browser-image-compression'
import lmFormatBytes from '@/mixins/lmFormatBytes.js'
import _ from 'lodash'

/**
* LmUploadFiles es un wrapper de vue-dropzone
*
* https://github.com/rowanwins/vue-dropzone GitHub
*
* https://rowanwins.github.io/vue-dropzone/docs/dist/index.html#/props Page
*
*/
export default {
  mixins: [lmFormatBytes, lmInputMixin, lmToasts],
  components: {
    vueDropzone,
    LmConfirmationModal,
    draggable
  },
  data () {
    return {
      allowedExtensionsHelperText: 'Puedes cargar cualquier tipo de archivo',
      files: this.value || [],
      fileModalPreview: {
        src: '',
        name: '',
        index: 0
      },
      fileToRemove: null,
      filesToRemoveFromServer: []
    }
  },
  computed: {
    urlToUploadFiles () {
      return process.env.VUE_APP_API_URL + 'upload/'
    },
    totalSizeIndicator () {
      return this.sizeIndicator(_.sumBy(this.files, 'size'))
    },
    totalSize () {
      return this.formatBytes(_.sumBy(this.files, 'size'))
    }
  },
  props: {
    /**
    * Setea options https://rowanwins.github.io/vue-dropzone/docs/dist/index.html#/props
    */
    dropzoneOptions: {
      type: Object,
      required: true
    },
    duplicateCheck: {
      type: Boolean,
      default: false
    }
  },
  watch: {
    dropzoneOptions (val) {
      // Actualizo las opciones de dropzone
      if (val && this.showComponent) {
        this.setupDropzoneComponent(val)
      }
    },
    files (val) {
      if (val) {
        this.$emit('input', this.files)
        this.$emit('onUpload', this.files)
      }
    },
    value (val) {
      if (val && val.length) {
        this.files = val
      }
    },
    filesToRemoveFromServer (val) {
      if (val && val.length) {
        this.$emit('filesToRemoveFromServer', val)
      }
    },
    showComponent (val) {
      if (val) {
        this.$nextTick(() => {
          this.setupDropzoneComponent(this.dropzoneOptions)
        })
      }
    }
  },
  created () {
    // Opciones fijas
    this.dropzoneOptions.dictFallbackMessage = 'El navegador no soporta este componente.'
    this.dropzoneOptions.dictRemoveFile = this.$t('remove_file')
    this.dropzoneOptions.dictInvalidFileType = 'Tipo de archivo no válido.'
    this.dropzoneOptions.dictMaxFilesExceeded = 'Máximo de archivos alcanzado.'
    this.dropzoneOptions.dictFileTooBig = 'El archivo es demasiado pesado.'

    // Asigno link a la dropzone para guardar los archivos
    this.dropzoneOptions.url = this.urlToUploadFiles

    this.dropzoneOptions.transformFile = async function (file, done) {
      try {
        const options = {
          maxSizeMB: 1,
          maxWidthOrHeight: 1920,
          useWebWorker: true
        }
        if (/^image\/\w+$/.test(file.type)) {
          file = await imageCompression(file, options)
        }
      } catch (error) {
        console.log(error)
      }
      return done(file)
    }
  },
  mounted () {
    // Configuro el componente
    if (this.showComponent) {
      this.setupDropzoneComponent(this.dropzoneOptions)
    }
  },
  methods: {
    // Observo los procesos para bloquar un posible "submit" sin terminar de cargar
    processing () {
      this.$emit('uploadProcessing', true)
    },
    complete () {
      this.$emit('uploadProcessing', false)
    },
    setupDropzoneComponent (options) {
      // Me aseguro de que siempre haya un url cargado para subir archivos
      if (!options?.url) {
        options.url = this.urlToUploadFiles
      }
      // Opciones dinamicas
      for (const opt in options) {
        if (opt === 'acceptedFiles') {
          const accepted = acceptedFilesGenerator(options[opt])
          if (accepted) {
            this.setDropzoneOption('acceptedFiles', accepted)
            this.allowedExtensionsHelperText = `Extensiones permitidas: ${options[opt].map(e => e.name.toLowerCase()).join(', ')}`
          } else {
            this.lmToastDangerMessage('Las extensiones de archivos permitidos no son correctas.', 'Hubo un error')
          }
        } else if (opt === 'maxFiles') {
          this.enable()
          if (options[opt] === '') {
            this.dropzoneOptions[opt] = null
            this.setDropzoneOption(opt, null)
          } else if (options[opt] === 0) {
            this.disable()
          } else {
            this.dropzoneOptions[opt] = options[opt]
            this.setDropzoneOption(opt, options[opt])
          }
        } else {
          this.setDropzoneOption(opt, options[opt])
        }
      }
    },
    setDropzoneOption (option, value) {
      this.$refs.dropzoneComponent.setOption(option, value)
    },
    fileAdded (file, error, xhr) {
      // Checkeo duplicados en modo edicion
      let isDuplicated = false
      let isEmpty = false
      let maxFiles = false
      this.files.forEach(f => {
        if (f.name === file.name && f.size === file.size && f.type === file.type) {
          isDuplicated = true
        }
      })
      // Checkeo maximo de archivos en modo edicion
      if (this.dropzoneOptions.maxFiles && this.files.length >= this.dropzoneOptions.maxFiles) {
        maxFiles = true
      }
      // Checkeo archivos vacios
      if (file.size === 0) {
        isEmpty = true
      }
      if (maxFiles) {
        this.lmRemoveFile(file)
        return this.lmToastDangerMessage(`No se pudo cargar el archivo "${file.name}"`, 'Máximo de archivos alcanzado.', 2000)
      }
      if (isEmpty) {
        this.lmRemoveFile(file)
        return this.lmToastDangerMessage(`No se pudo cargar el archivo "${file.name}"`, 'Archivo vacío', 2000)
      }
      if (isDuplicated) {
        this.lmRemoveFile(file)
        return this.fileDuplicated(file)
      }
    },
    fileRemoved (file, error, xhr) {
      // this.$emit('vdropzone-removed-file', file, error, xhr)
      if (file.accepted) {
        const fileToRemove = this.files.find(f => f.uuid === file.upload.uuid)
        if (fileToRemove) {
          this.filesToRemoveFromServer.push(fileToRemove)
        }
        this.files = this.files.filter(f => f.uuid !== file.upload.uuid)
      }
    },
    fileCreated (file, response) {
      // this.$emit('vdropzone-success', file, response)
      if (file.accepted) {
        this.files.push({
          uuid: file.upload.uuid,
          name: file.name,
          path: response.path,
          size: file.size,
          type: file.type,
          ext: file.name.split('.').at(-1)
        })
      }
      this.lmToastSuccessMessage(`Se cargó "${file.name}" correctamente!`, 'Acción exitosa', 2000)
    },
    fileDuplicated (file) {
      // this.$emit('vdropzone-duplicate-file', file)
      this.lmToastDangerMessage(`No se pudo cargar el archivo "${file.name}"`, 'Archivo duplicado', 2000)
    },
    errorHappend (file, message, xhr) {
      // this.$emit('vdropzone-error', file, message, xhr)
      this.lmToastDangerMessage(`No se pudo cargar el archivo "${file.name}"`, message, 2000)
      this.lmRemoveFile(file)
    },

    lmManuallyAddFile (fileOptions, url) {
      this.$refs.dropzoneComponent.manuallyAddFile(fileOptions, url)
    },
    lmRemoveAllFiles () {
      this.lmToastSuccessMessage(`Se eliminaron ${this.files.length} archivos correctamente!`, 'Acción exitosa', 2000)
      this.filesToRemoveFromServer = this.filesToRemoveFromServer.concat(this.files)
      this.files = []
      this.$refs.dropzoneComponent.removeAllFiles()
    },
    lmRemoveFile (file) {
      this.$refs.dropzoneComponent.removeFile(file)
      this.fileToRemove = null
    },
    disable (fileOptions, url) {
      this.$refs.dropzoneComponent.disable()
    },
    enable (fileOptions, url) {
      this.$refs.dropzoneComponent.enable()
    },
    async removeAllFilesModal () {
      try {
        this.$refs.removeAllFilesModal.setMessageProps('¿Está seguro que desea eliminar todos los archivos cargados?')
        await this.$refs.removeAllFilesModal.lmShow()
      } catch (error) {
        console.log(error)
      }
    },
    removeUploadFile (file) {
      const acceptedFiles = this.$refs.dropzoneComponent.getAcceptedFiles()
      this.fileToRemove = acceptedFiles.find(af => af.upload.uuid === file.uuid) || null
      if (this.fileToRemove === null) {
        this.files = this.files.filter(f => f.uuid !== file.uuid)
        this.filesToRemoveFromServer.push(file)
      } else {
        this.lmRemoveFile(this.fileToRemove)
      }
      this.lmToastSuccessMessage(`Se eliminó "${file.name}" correctamente!`, 'Acción exitosa', 2000)
    },
    setFileInModalPreview (file, index) {
      this.fileModalPreview.src = process.env.VUE_APP_API_URL_STATIC_ASSETS + file.path
      this.fileModalPreview.name = file.name
      this.fileModalPreview.index = index
    },
    onNextFile () {
      const file = this.files[this.fileModalPreview.index + 1]
      this.fileModalPreview.src = process.env.VUE_APP_API_URL_STATIC_ASSETS + file.path
      this.fileModalPreview.name = file.name
      this.fileModalPreview.index = this.fileModalPreview.index + 1
    },
    onPrevFile () {
      const file = this.files[this.fileModalPreview.index - 1]
      this.fileModalPreview.src = process.env.VUE_APP_API_URL_STATIC_ASSETS + file.path
      this.fileModalPreview.name = file.name
      this.fileModalPreview.index = this.fileModalPreview.index - 1
    }
  }
}
</script>

<style lang="sass" scoped>
.vue-dropzone.dropzone
  border: 4px dashed #e5e5e5
  border-radius: 5px
  cursor: pointer
  &.disabled
    cursor: no-drop
    background: #f6f6f6
  &.is-invalid
    border: 4px dashed #fd8e98
.circle
  background: #ababab
  color: white
  border-radius: 50%
  width: 20px
  height: 20px
  text-align: center
  padding-top: 2px
.trash-icon
  cursor: pointer
  width: 18px
  height: 18px
  &:hover
    color: #000
.next-btn, .prev-btn
  color: white
  font-size: 1.2rem
.prev-btn
  left: 10px
.next-btn
  right: 10px
</style>
